import { GymnastFriend } from '../types';

interface GymnastAvatarProps {
  friend: GymnastFriend;
  size?: 'sm' | 'md' | 'lg';
  pose?: 'cheer' | 'bridge' | 'ready';
}

export default function GymnastAvatar({ friend, size = 'md', pose = 'cheer' }: GymnastAvatarProps) {
  const sizeMap = {
    sm: 'w-16 h-16',
    md: 'w-28 h-28',
    lg: 'w-40 h-40',
  };

  const leotardColor = friend.favoriteColor || '#FF69B4';

  return (
    <div className={`relative flex items-center justify-center ${sizeMap[size]}`}>
      <svg
        viewBox="0 0 120 120"
        className="w-full h-full drop-shadow-md transition-transform duration-300 hover:scale-105"
      >
        <defs>
          <linearGradient id={`leotard-grad-${friend.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#7FFFD4" />
            <stop offset="40%" stopColor={leotardColor} />
            <stop offset="100%" stopColor="#008080" />
          </linearGradient>
          <linearGradient id={`ribbon-grad-${friend.id}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#00CED1" />
            <stop offset="100%" stopColor={leotardColor} />
          </linearGradient>
        </defs>

        {/* Glow Aura */}
        <circle cx="60" cy="60" r="54" fill={leotardColor} opacity="0.15" />
        
        {/* Hair Back */}
        <path
          d="M38 52 C 32 30, 88 30, 82 52 C 86 65, 80 80, 78 84 C 74 74, 80 62, 75 55 C 68 50, 52 50, 45 55 C 40 62, 46 74, 42 84 C 40 80, 34 65, 38 52 Z"
          fill="#5C3826"
        />
        {/* High Ponytail with Turquoise Ribbon */}
        <circle cx="60" cy="24" r="8" fill={`url(#ribbon-grad-${friend.id})`} />
        <ellipse cx="68" cy="18" rx="7" ry="12" fill="#5C3826" transform="rotate(30 68 18)" />

        {/* Head */}
        <circle cx="60" cy="50" r="18" fill="#FFDFC4" />

        {/* Cheeks */}
        <circle cx="48" cy="54" r="3" fill="#FF9EAA" opacity="0.6" />
        <circle cx="72" cy="54" r="3" fill="#FF9EAA" opacity="0.6" />

        {/* Friendly Eyes */}
        <circle cx="53" cy="48" r="2.5" fill="#2C2C2C" />
        <circle cx="67" cy="48" r="2.5" fill="#2C2C2C" />
        <circle cx="54" cy="47" r="0.8" fill="#FFFFFF" />
        <circle cx="68" cy="47" r="0.8" fill="#FFFFFF" />

        {/* Big Happy Smile */}
        <path d="M55 55 Q 60 60 65 55" stroke="#2C2C2C" strokeWidth="2" fill="none" strokeLinecap="round" />

        {/* Bangs */}
        <path d="M44 42 Q 60 48 76 42 Q 68 34 60 34 Q 52 34 44 42 Z" fill="#5C3826" />

        {/* Torso & Multi-shade Leotard */}
        <path
          d="M49 68 Q 60 66 71 68 L 74 95 Q 60 97 46 95 Z"
          fill={friend.id === 'friend-backbend' ? `url(#leotard-grad-${friend.id})` : leotardColor}
        />
        {/* Sparkle details on leotard */}
        <polygon points="60,74 61,77 64,78 61,79 60,82 59,79 56,78 59,77" fill="#E0FFFF" opacity="0.95" />
        <circle cx="54" cy="84" r="1.5" fill="#E0FFFF" opacity="0.8" />
        <circle cx="66" cy="84" r="1.5" fill="#E0FFFF" opacity="0.8" />

        {/* Arms Pose */}
        {pose === 'cheer' && (
          <>
            {/* Arms up high waving with joy */}
            <path d="M49 70 Q 32 50 25 36" stroke="#FFDFC4" strokeWidth="6" strokeLinecap="round" fill="none" />
            <path d="M71 70 Q 88 50 95 36" stroke="#FFDFC4" strokeWidth="6" strokeLinecap="round" fill="none" />
            <circle cx="24" cy="35" r="4" fill="#FFDFC4" />
            <circle cx="96" cy="35" r="4" fill="#FFDFC4" />
          </>
        )}

        {pose === 'ready' && (
          <>
            {/* Hands on hips ready to practice */}
            <path d="M49 70 Q 36 76 46 86" stroke="#FFDFC4" strokeWidth="6" strokeLinecap="round" fill="none" />
            <path d="M71 70 Q 84 76 74 86" stroke="#FFDFC4" strokeWidth="6" strokeLinecap="round" fill="none" />
          </>
        )}
      </svg>
      {/* Badge Emoji */}
      <span className="absolute -bottom-1 -right-1 text-2xl drop-shadow select-none">
        {friend.badgeEmoji}
      </span>
    </div>
  );
}
